import dayjs from 'dayjs';
import { atom, useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import type {
  EventsPerDayDTO,
  ServiceUsagePerDayDTO,
  UsageLimitType
} from '@/pages/Dashboard/Dashboard.types';
import { useFetchCaseMetrics } from '@/pages/Dashboard/DashboardCases/DashboardCases.store';
import type { ProductType } from '@/pages/Organizations';
import { useFetchV2Tickets } from '@/pages/Tickets/Tickets.store';
import { axiosClient } from '@/services/BaseApi.service';
import type UseReportsDataParams from '../ReportsTemplate.types';
import type {
  ReportInsightsDTO,
  TicketMetricsByPriority
} from '../ReportsTemplate.types';
import type { CasesGroupByDTO } from './ReportsPages/ReportsPage.types';

export const reportsOrgLabelAtom = atom<string>('');
export const reportsStartDateAtom = atom<string>('2024-12-01');
export const reportsEndDateAtom = atom<string>('2024-12-31');
export const reportsFrequencyAtom = atom<string>('monthly');
export const reportsTimeFrameAtom = atom<number>(30);

export const reportsUsageDataAtom = atom<ServiceUsagePerDayDTO[]>();
export const reportsNetworkAvgAtom = atom<number>(0);
export const reportsLogAvgAtom = atom<number>(0);
export const reportsUsageLimitsDataAtom = atom<UsageLimitType[]>();
export const reportsProductsDataAtom = atom<ProductType[]>([]);
export const ticketedCasesByDateAtom = atom<CasesGroupByDTO[]>([]);

export const useReportData = ({
  organization,
  organizationLabel,
  startDate,
  endDate
}: UseReportsDataParams) => {
  const [, setUsagePerDay] = useAtom(reportsUsageDataAtom);
  const [, setUsageLimits] = useAtom(reportsUsageLimitsDataAtom);
  const [, setProductsData] = useAtom(reportsProductsDataAtom);
  const [insights, setInsights] = useState<ReportInsightsDTO>();
  const [orgAllMetrics, setOrgAllMetrics] = useState<TicketMetricsByPriority[]>(
    []
  );
  const [eventsPerDay, setEventsPerDay] = useState<EventsPerDayDTO[]>([]);
  const [usageData] = useAtom(reportsUsageDataAtom);
  const [usageLimitsData] = useAtom(reportsUsageLimitsDataAtom);
  const [productsData] = useAtom(reportsProductsDataAtom);

  const startAndEndDateDiff = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;

  const minimumStartDate =
    startAndEndDateDiff < 7
      ? dayjs(startDate)
          .subtract(7 - startAndEndDateDiff, 'day')
          .format('YYYY-MM-DD')
      : startDate; // Minimum date difference for graphs over timeframes less than 7 days

  const inclusiveEndDate = dayjs(endDate)
    .add(1, 'day')
    .format(YEAR_MONTH_DAY_FORMAT); // Adding 1 day to include the whole end date

  const { data: ticketedCasesPerDay } = useFetchCaseMetrics({
    created_at: { from_date: minimumStartDate, to_date: endDate },
    group_by: 'day',
    ticketed: true,
    organization_label: organizationLabel
  });

  const { data: casesPerDay } = useFetchCaseMetrics({
    created_at: { from_date: minimumStartDate, to_date: endDate },
    group_by: 'day',
    organization_label: organizationLabel
  });

  const searchParams = {
    published_at__gte: minimumStartDate,
    published_at__lte: inclusiveEndDate,
    organization__label: organizationLabel,
    ticket_type__in: ['1'] // Filter for THREAT tickets only (1 = THREAT, 2 = SERVICE)
  };

  const { data: tickets, isFetching: isFetchingTickets } = useFetchV2Tickets({
    enabled: true,
    searchParams: searchParams,
    fetchAllPages: true
  });

  const fetchReportData = useCallback(async () => {
    try {
      const eventsResponse = await axiosClient.get(
        `/organizations/${organizationLabel}/events/per_day?start_date=${minimumStartDate}&end_date=${endDate}`
      );
      const insightsResponse = await axiosClient.get(
        `/report_insights/by_month/${dayjs(startDate).format('YYYY-MM')}`
      );
      const usageResponse = await axiosClient.get(
        `/organizations/${organizationLabel}/service_usage?start_date=${dayjs(minimumStartDate).subtract(startAndEndDateDiff, 'days').format(YEAR_MONTH_DAY_FORMAT)}&end_date=${endDate}`
      );
      const usageLimitResponse = await axiosClient.get(
        `/organizations/${organizationLabel}/products/service_usage_limits`
      );
      const productsResponse = await axiosClient.get(
        `/organizations/${organizationLabel}/products?skip=0&limit=100`
      );
      const orgAllMetricsData = await axiosClient.get(
        `/organizations/${organizationLabel}/cases/metrics/mttx?start_date=${startDate}&end_date=${endDate}&group_by_priority=true`
      );

      setEventsPerDay(eventsResponse.data);
      setUsagePerDay(usageResponse.data);
      setUsageLimits(usageLimitResponse.data);
      setProductsData(productsResponse.data);
      setOrgAllMetrics(orgAllMetricsData.data);
      setInsights(insightsResponse.data);

      return;
    } catch (error) {
      console.error('Error fetching report data:', error);
      return;
    }
  }, [startDate, endDate, organization, organizationLabel]);

  const { data: casesByTitle } = useFetchCaseMetrics({
    created_at: { from_date: startDate, to_date: endDate },
    group_by: 'case_title',
    organization_label: organizationLabel
  });
  const { data: ticketedCasesByTitle } = useFetchCaseMetrics({
    created_at: { from_date: startDate, to_date: endDate },
    group_by: 'case_title',
    ticketed: true,
    organization_label: organizationLabel
  });

  const ticketedLogCases = ticketedCasesByTitle.filter(
    (item) => item.log_count > 0
  );
  const logCases = casesByTitle.filter((item) => item.log_count > 0);

  const logCasesTableData = () => {
    const rowsByTitle = new Map(
      [...logCases, ...ticketedLogCases].map(({ value }) => [
        value,
        { value: value, count: 0, ticketedCount: 0 }
      ])
    );

    for (const { value, log_count } of logCases) {
      rowsByTitle.get(value)!.count += log_count;
    }

    for (const { value, log_count } of ticketedLogCases) {
      rowsByTitle.get(value)!.ticketedCount += log_count;
    }

    const sortedTableData = sortBy(
      [...rowsByTitle.values()],
      (item) => item.count
    ).reverse();

    return sortedTableData.map((item) => ({
      ...item,
      id: uuidv4()
    }));
  };

  const logTicketedCasesTableData = () => {
    const sortedTableData = sortBy(
      [...ticketedLogCases],
      (item) => item.log_count
    ).reverse();

    return sortedTableData.map((item) => ({
      ...item,
      count: item.log_count,
      id: uuidv4()
    }));
  };

  const ticketedNetworkCases = ticketedCasesByTitle.filter(
    (item) => item.network_count > 0
  );
  const networkCases = casesByTitle.filter((item) => item.network_count > 0);

  const networkCasesTableData = () => {
    const rowsByTitle = new Map(
      [...networkCases, ...ticketedNetworkCases].map(({ value }) => [
        value,
        { value: value, count: 0, ticketedCount: 0 }
      ])
    );

    for (const { value, network_count } of networkCases) {
      rowsByTitle.get(value)!.count += network_count;
    }

    for (const { value, network_count } of ticketedNetworkCases) {
      rowsByTitle.get(value)!.ticketedCount += network_count;
    }

    const sortedTableData = sortBy(
      [...rowsByTitle.values()],
      (item) => item.count
    ).reverse();

    return sortedTableData.map((item) => ({
      ...item,
      id: uuidv4()
    }));
  };

  const networkTicketedCasesTableData = () => {
    const sortedTableData = sortBy(
      [...ticketedNetworkCases],
      (item) => item.network_count
    ).reverse();

    return sortedTableData.map((item) => ({
      ...item,
      count: item.network_count,
      id: uuidv4()
    }));
  };

  return {
    logCasesTableData: logCasesTableData(),
    networkCasesTableData: networkCasesTableData(),
    logTicketedCasesTableData: logTicketedCasesTableData(),
    networkTicketedCasesTableData: networkTicketedCasesTableData(),
    eventsPerDay,
    usageData,
    usageLimitsData,
    productsData,
    tickets,
    orgAllMetrics,
    insights,
    fetchReportData,
    ticketedCasesPerDay,
    casesPerDay,
    isFetchingTickets
  };
};

export default useReportData;
